import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import { QBox } from '@qualio/ui-components';
import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';
import { Context } from 'ckeditor5';
import { saveAs } from 'file-saver';
import {
  documentApi,
  QualioAttachmentInfoResponse,
  QualioAttachmentPreviewType,
} from '../../../api/document';
import { QualioDocument } from '../../../api/model/document';
import { qualioAttachmentService } from '../../../api/qualioAttachmentService';
import {
  API_FETCH_ATTACHMENT_BLOB_ERROR,
  logCustomError,
} from '../../../messages/LogErrorMessages';
import {
  setElementDisabledState,
  toggleDisableClass,
  toggleFakeLinkDisableClass,
} from '../../../util/HTMLElementUtils';
import { printFile } from '../../../util/PrintFile';
import { refreshDisplayMode } from '../../../util/SidebarUtils';
import {
  getAttachmentPreviewUrl,
  getAttachmentUrl,
} from '../../../util/UrlUtils';
import { FileDocumentCommentsWrapper } from './FileDocumentCommentsWrapper';
import { FileDocumentLoadingIndicator } from './FileDocumentLoadingIndicator';
import styles from './FileDocumentPreviewer.module.css';
import { ImageDocumentViewer } from './ImageDocumentViewer';
import { PDFDocumentViewer } from './PDFDocumentViewer';

const attachmentConversionSuccessful = (
  attachmentConversionStatus: QualioAttachmentInfoResponse,
): boolean =>
  attachmentConversionStatus &&
  qualioAttachmentService.attachmentPreviewSuccessful(
    attachmentConversionStatus,
  );

export const FileDocumentPreviewer: React.FC<{
  currentUser: CurrentUser;
  attachmentId: string;
  attachmentFilename?: string;
  showDownloadButton: boolean;
  collaborationToken?: string;
  commentsPermission?: number;
  fileDocument?: QualioDocument;
  isInlineAttachment?: boolean;
  handleSessionDisconnectionEvent?: (didReceive: boolean) => void;
}> = ({
  currentUser,
  attachmentId,
  attachmentFilename = '',
  showDownloadButton,
  collaborationToken,
  commentsPermission = 0,
  fileDocument,
  isInlineAttachment,
  handleSessionDisconnectionEvent,
}) => {
  const [previewReady, setPreviewReady] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<boolean>(false);
  const [context, setContext] = useState<Context>();
  const [previewType, setPreviewType] = useState<QualioAttachmentPreviewType>();
  const [mimeType, setMimeType] = useState<string>('');
  const [filename, setFilename] = useState<string>('');

  const attachmentPreviewUrl = useMemo(() => {
    return getAttachmentPreviewUrl(attachmentId);
    // eslint-disable-next-line
  }, [attachmentId, previewReady, previewError]);

  const attachmentUrl = useMemo(() => {
    return getAttachmentUrl(attachmentId);
  }, [attachmentId]);

  const downloadAttachmentUrl = useMemo(
    () => getAttachmentUrl(attachmentId, true),
    [attachmentId],
  );

  useEffect(() => {
    const awaitConversionResults = async () => {
      if (attachmentId) {
        setPreviewReady(false);
        setPreviewError(false);
        const conversionResult =
          await qualioAttachmentService.pollAttachmentInfoUntilCompletion(
            attachmentId,
          );
        if (
          conversionResult &&
          attachmentConversionSuccessful(conversionResult)
        ) {
          setPreviewReady(true);
          if (conversionResult.preview_type) {
            setPreviewType(conversionResult.preview_type);
          }
          setMimeType(
            qualioAttachmentService.determinePreviewContentMimeType(
              conversionResult.filename,
            ),
          );
          setFilename(conversionResult.filename);
        } else {
          setPreviewError(true);
        }
      }
    };

    void awaitConversionResults();
  }, [attachmentId]);

  const handleDownload = () => {
    documentApi
      .fetchAttachmentDataAsBlob(attachmentId, mimeType)
      .then((res) => {
        if (res && filename) {
          saveAs(res, filename);
        }
      })
      .catch((error) => {
        logCustomError(API_FETCH_ATTACHMENT_BLOB_ERROR, {
          error,
          entityId: +attachmentId,
        });
      });
  };

  const handlePrint = () => {
    void printFile(attachmentPreviewUrl, previewType);
  };

  const exportButton = document.getElementById('documentEditorExport');
  const convertButton = document.getElementById('documentConversion');

  const showComments =
    commentsPermission > 0 &&
    collaborationToken &&
    fileDocument?.is_most_recent_version;

  if (previewReady) {
    toggleDisableClass(exportButton, false);
    setElementDisabledState(convertButton, false);
    toggleFakeLinkDisableClass(convertButton, false);
  }

  const resizeOnZoom = (zoom: number) => {
    refreshDisplayMode({
      instances: context ? [context] : [],
      windowWidth: window.innerWidth,
      forceNarrow: zoom > 1.1,
      breakpointOverride: 1800,
    });
  };

  const isXlsx = useMemo(() => {
    return attachmentFilename.split('.').pop() === 'xlsx';
  }, [attachmentFilename]);

  return (
    <>
      {!previewError && !previewReady && <FileDocumentLoadingIndicator />}
      {previewError && (
        <p
          id={'errorMessage'}
          data-cy="previewError"
          className={styles['file-preview-info-text']}
        >
          <Trans
            i18nKey="errors.unsupportedPreviewWithActions"
            components={{
              br: <br />,
              openLink: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={attachmentUrl}
                />
              ),
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              downloadLink: <a href={downloadAttachmentUrl} />,
            }}
          />
        </p>
      )}
      {previewReady && !showComments && (
        <QBox id={'fileDocumentPreviewer'}>
          {previewType === 'document' && (
            <PDFDocumentViewer
              attachmentPreviewUrl={attachmentPreviewUrl}
              handleDownload={handleDownload}
              showDownloadButton={showDownloadButton}
              commentsPermission={0}
              handlePrint={isInlineAttachment ? handlePrint : undefined}
              isXlsx={isXlsx}
            />
          )}
          {previewType === 'image' && (
            <ImageDocumentViewer
              attachmentPreviewUrl={attachmentPreviewUrl}
              handleDownload={handleDownload}
              showDownloadButton={showDownloadButton}
              commentsPermission={0}
              handlePrint={isInlineAttachment ? handlePrint : undefined}
            />
          )}
        </QBox>
      )}
      {previewReady && showComments && (
        <QBox
          id={'fileDocumentPreviewer'}
          className={styles['file-document-previewer__wrapper-sidebar']}
        >
          <FileDocumentCommentsWrapper
            currentUser={currentUser}
            attachmentFilename={attachmentFilename}
            attachmentId={attachmentId}
            fileDocument={fileDocument}
            collaborationToken={collaborationToken}
            commentsPermission={commentsPermission}
            contextHandler={setContext}
            handleSessionDisconnectionEvent={handleSessionDisconnectionEvent}
          >
            {previewType === 'document' && (
              <PDFDocumentViewer
                attachmentPreviewUrl={attachmentPreviewUrl}
                handleDownload={handleDownload}
                showDownloadButton={showDownloadButton}
                commentsPermission={commentsPermission}
                handleZoomChange={resizeOnZoom}
                renderFileDocumentLoadingIndicator={false}
                isXlsx={isXlsx}
              />
            )}
            {previewType === 'image' && (
              <ImageDocumentViewer
                attachmentPreviewUrl={attachmentPreviewUrl}
                handleDownload={handleDownload}
                showDownloadButton={showDownloadButton}
                commentsPermission={commentsPermission}
                handleZoomChange={resizeOnZoom}
              />
            )}
          </FileDocumentCommentsWrapper>
        </QBox>
      )}
    </>
  );
};
